import React, { useEffect, useId, useRef, useState } from 'react';
import { createServiceRequest } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { toast } from '../../lib/toast';
import './ServiceRequestButtons.css';

const PENDING_CLEAR_MS = 5 * 60 * 1000; // mirror the staff acknowledgement window

const ChevronIcon = ({ open }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ transition: 'transform 0.25s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ServiceRequestButtons = ({ restaurantId, tableNumber }) => {
  const { t } = useTranslation();
  const headingId = useId();
  const [loading, setLoading] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(new Set());
  const [expanded, setExpanded] = useState(false);
  /* Hold the per-type clear timers in a ref so we can cancel them all on
     unmount. Previously these were dangling — if the diner closed the tab
     within 5 minutes of a request, the timer fired against an unmounted
     component and React warned. */
  const clearTimersRef = useRef({});

  useEffect(() => () => {
    Object.values(clearTimersRef.current).forEach(id => id && clearTimeout(id));
    clearTimersRef.current = {};
  }, []);

  const handleServiceRequest = async (requestType, displayName) => {
    if (pendingRequests.has(requestType)) {
      toast.error(t('service.alreadyPending', { defaultValue: `You already have a pending ${displayName} request`, displayName }));
      return;
    }

    setLoading(requestType);

    try {
      await createServiceRequest({
        restaurantId: Number(restaurantId),
        tableNumber: String(tableNumber),
        requestType
      });

      setPendingRequests((prev) => new Set(prev).add(requestType));

      // Short ack toast — the long-lived "we got it" feedback lives in the
      // persistent .service-status row below, not in a 4s toast the user
      // might miss.
      toast.success(
        t('service.requestSent', { defaultValue: 'Sent — staff notified' }),
        { duration: 2200 }
      );

      // Replace any prior timer for the same type so we don't leak.
      if (clearTimersRef.current[requestType]) {
        clearTimeout(clearTimersRef.current[requestType]);
      }
      clearTimersRef.current[requestType] = setTimeout(() => {
        delete clearTimersRef.current[requestType];
        setPendingRequests((prev) => {
          const next = new Set(prev);
          next.delete(requestType);
          return next;
        });
      }, PENDING_CLEAR_MS);
    } catch (error) {
      console.error('Service request error:', error);

      if (error.response?.status === 429) {
        toast.error(t('service.tooManyRequests', { defaultValue: 'Too many requests. Please wait a moment.' }));
      } else if (error.response?.status === 409) {
        toast.error(t('service.duplicateRequest', { defaultValue: 'You already have a pending request of this type' }));
        setPendingRequests((prev) => new Set(prev).add(requestType));
      } else {
        toast.error(t('service.requestFailed', { defaultValue: 'Failed to send request. Please try again.' }));
      }
    } finally {
      setLoading(null);
    }
  };

  const acknowledgeRequest = (requestType) => {
    if (clearTimersRef.current[requestType]) {
      clearTimeout(clearTimersRef.current[requestType]);
      delete clearTimersRef.current[requestType];
    }
    setPendingRequests((prev) => {
      const next = new Set(prev);
      next.delete(requestType);
      return next;
    });
  };

  const buttons = [
    {
      type: 'call_waiter',
      icon: '👋',
      label: t('service.callWaiter', { defaultValue: 'Call Waiter' }),
      color: 'primary',
      description: t('service.callWaiterDesc', { defaultValue: 'Need assistance?' }),
      statusLabel: t('service.statusWaiterPending', { defaultValue: 'Waiter on the way…' }),
    },
    {
      type: 'request_bill',
      icon: '💳',
      label: t('service.requestBill', { defaultValue: 'Request Bill' }),
      color: 'secondary',
      description: t('service.requestBillDesc', { defaultValue: 'Ready to pay?' }),
      statusLabel: t('service.statusBillPending', { defaultValue: 'Bill on the way…' }),
    },
    {
      type: 'need_help',
      icon: '❓',
      label: t('service.needHelp', { defaultValue: 'Need Help' }),
      color: 'warning',
      description: t('service.needHelpDesc', { defaultValue: 'Have a question?' }),
      statusLabel: t('service.statusHelpPending', { defaultValue: 'Help requested…' }),
    }
  ];

  const quickActions = [
    { type: 'water', icon: '💧', label: t('service.water', { defaultValue: 'Water' }), statusLabel: t('service.statusWaterPending', { defaultValue: 'Water requested…' }) },
    { type: 'napkins', icon: '🧻', label: t('service.napkins', { defaultValue: 'Napkins' }), statusLabel: t('service.statusNapkinsPending', { defaultValue: 'Napkins requested…' }) },
    { type: 'utensils', icon: '🍴', label: t('service.utensils', { defaultValue: 'Utensils' }), statusLabel: t('service.statusUtensilsPending', { defaultValue: 'Utensils requested…' }) },
  ];

  const hasPending = pendingRequests.size > 0;

  /* Build the persistent status rows. One row per pending request type;
     stays visible inside the panel until either the 5-minute timeout fires
     or the user acknowledges. Survives the toast auto-dismiss because it's
     part of the panel itself, not a transient notification. (Not memoized:
     the `buttons` and `quickActions` arrays are rebuilt every render anyway,
     so a useMemo here would never cache.) */
  const pendingStatusRows = (() => {
    const byType = new Map();
    [...buttons, ...quickActions].forEach(b => byType.set(b.type, b));
    return [...pendingRequests].map(type => byType.get(type) || { type, label: type, icon: '🔔', statusLabel: type });
  })();

  return (
    <section
      className={`service-request-buttons ${expanded ? 'service-request-buttons--expanded' : ''}`}
      aria-labelledby={headingId}
    >
      <h2 id={headingId} className="visually-hidden">
        {t('service.regionLabel', { defaultValue: 'Table service requests' })}
      </h2>

      <button
        type="button"
        className="service-toggle"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <div className="service-toggle-left">
          <span className="service-toggle-icon" aria-hidden="true">🔔</span>
          <span className="service-toggle-text">
            {t('service.needSomething', { defaultValue: 'Need Something?' })}
          </span>
          {hasPending && (
            <span
              className="service-toggle-badge"
              aria-live="polite"
              aria-label={t('service.pendingCount', { count: pendingRequests.size, defaultValue: `${pendingRequests.size} pending` })}
            >
              {pendingRequests.size}
            </span>
          )}
        </div>
        <ChevronIcon open={expanded} />
      </button>

      <div className={`service-collapsible ${expanded ? 'service-collapsible--open' : ''}`}>
        <div className="service-collapsible-inner">
          {/* Persistent status — one row per active pending request. */}
          {pendingStatusRows.length > 0 && (
            <ul className="service-status-list" aria-live="polite">
              {pendingStatusRows.map((row) => (
                <li key={row.type} className="service-status">
                  <span className="service-status-dot" aria-hidden="true" />
                  <span className="service-status-text">{row.statusLabel}</span>
                  <button
                    type="button"
                    className="service-status-ack"
                    onClick={() => acknowledgeRequest(row.type)}
                    aria-label={t('service.acknowledge', { defaultValue: 'Acknowledge' })}
                  >
                    {t('service.acknowledge', { defaultValue: 'Got it' })}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="service-buttons-grid">
            {buttons.map((button) => {
              const isPending = pendingRequests.has(button.type);
              const isLoading = loading === button.type;

              return (
                <button
                  key={button.type}
                  type="button"
                  className={`service-btn ${button.color} ${isPending ? 'pending' : ''} ${isLoading ? 'loading' : ''}`}
                  onClick={() => handleServiceRequest(button.type, button.label)}
                  disabled={isLoading || isPending}
                  aria-pressed={isPending}
                >
                  <div className="service-btn-content">
                    <span className="service-icon" aria-hidden="true">{button.icon}</span>
                    <div className="service-text">
                      <span className="service-label">{button.label}</span>
                      <span className="service-description">{button.description}</span>
                    </div>
                    {isPending && (
                      <span className="pending-indicator">
                        <span className="pending-dot" />
                        {t('service.pending', { defaultValue: 'Pending' })}
                      </span>
                    )}
                    {isLoading && <div className="btn-spinner" aria-hidden="true" />}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="quick-actions">
            {quickActions.map((qa) => {
              const isPending = pendingRequests.has(qa.type);
              const isLoading = loading === qa.type;
              return (
                <button
                  key={qa.type}
                  type="button"
                  className={`quick-action-btn ${isPending ? 'pending' : ''}`}
                  onClick={() => handleServiceRequest(qa.type, qa.label)}
                  disabled={isLoading || isPending}
                  aria-pressed={isPending}
                >
                  <span className="quick-icon" aria-hidden="true">{qa.icon}</span>
                  <span className="quick-label">{qa.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceRequestButtons;
